import { motion } from "framer-motion";
import { Target, Eye, Gem, ShieldCheck, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHero } from "@/components/shared/PageHero";
import { Eyebrow } from "@/components/shared/Eyebrow";
import { Reveal } from "@/components/shared/Reveal";
import { CTA } from "@/components/home/CTA";
import { useSeo } from "@/components/shared/useSeo";

const HERO = "https://images.unsplash.com/photo-1717386255773-1e3037c81788?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const IMG2 = "https://images.unsplash.com/photo-1565954786194-d22abeaac3ae?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const VALUES = [
  { icon: Gem, t: "Kalite", d: "Standartlara uygun, güvenli ve uzun ömürlü çözümler üretiriz." },
  { icon: ShieldCheck, t: "Güven", d: "Şeffaflık ve taahhüde bağlılık ilişkilerimizin temelidir." },
  { icon: Target, t: "Uzmanlık", d: "Teknik derinlik ve sürekli gelişim odaklı bir ekibiz." },
  { icon: Eye, t: "Sürdürülebilirlik", d: "Enerji verimli ve sürdürülebilir sistemleri önceliklendiririz." },
];

export default function Kurumsal() {
  useSeo({
    title: "Kurumsal | Sistem Elektrik ve Mühendislik",
    description: "Sistem Elektrik ve Mühendislik'in mühendislik felsefesi, vizyonu, misyonu ve değerleri.",
    canonical: "https://sistemmuhendislik.com/kurumsal",
  });

  return (
    <>
      <PageHero
        eyebrow="KURUMSAL"
        title="Mühendislik disiplinini işimizin merkezine koyuyoruz."
        subtitle="Endüstriyel elektrik ve otomasyon alanında güvenilir bir çözüm ortağı."
        image={HERO}
        crumb={[{ label: "Kurumsal" }]}
      />

      {/* Intro / philosophy */}
      <section className="bg-light py-24 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <Eyebrow className="mb-6">MÜHENDİSLİK FELSEFEMİZ</Eyebrow>
            <Reveal>
              <h2 className="font-head font-extrabold text-ink tracking-tight text-4xl md:text-5xl leading-[1.05]">
                Karmaşık projeleri sade ve güvenilir çözümlere dönüştürüyoruz.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-ink/70 text-lg leading-relaxed">
                Sistem Elektrik ve Mühendislik, endüstriyel elektrik sistemleri, otomasyon ve
                mühendislik hizmetlerinde uçtan uca çözümler sunar. Her projeyi; doğru analiz,
                titiz tasarım ve sahada disiplinli uygulama ile hayata geçiririz.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-4 text-ink/70 text-lg leading-relaxed">
                Mühendislik disiplinini, teknolojiyi ve güveni bir araya getirerek; verimli,
                güvenilir ve sürdürülebilir sistemler inşa ediyoruz.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <motion.div className="relative aspect-[4/3] overflow-hidden bg-navy"
              initial={{ opacity: 0, scale: 1.05 }} whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }} transition={{ duration: 1 }}>
              <img src={IMG2} alt="Mühendislik" className="w-full h-full object-cover" loading="lazy" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision / Mission */}
      <section className="bg-dark text-white py-24 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          <div className="bg-dark p-10 md:p-14">
            <Eyebrow dark className="mb-6">VİZYON</Eyebrow>
            <p className="text-2xl md:text-3xl font-head font-semibold leading-snug">
              Endüstriyel elektrik ve otomasyonda, teknolojiyi mühendislikle buluşturan öncü bir çözüm ortağı olmak.
            </p>
          </div>
          <div className="bg-dark p-10 md:p-14">
            <Eyebrow dark className="mb-6">MİSYON</Eyebrow>
            <p className="text-2xl md:text-3xl font-head font-semibold leading-snug">
              Müşterilerimize güvenilir, verimli ve sürdürülebilir sistemler sunarak projelerine değer katmak.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-white py-24 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <Eyebrow className="mb-6">DEĞERLERİMİZ</Eyebrow>
          <Reveal>
            <h2 className="font-head font-extrabold text-ink tracking-tight text-4xl md:text-5xl max-w-2xl leading-[1.05]">
              Bizi biz yapan ilkeler.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10 border border-ink/10">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.div key={v.t} className="group bg-white p-8 hover:bg-light transition-colors duration-300"
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}>
                  <div className="w-12 h-12 flex items-center justify-center border border-ink/15 text-tech group-hover:text-cyan group-hover:border-cyan transition-colors">
                    <Icon className="w-6 h-6" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-head font-bold text-ink text-xl mt-5">{v.t}</h3>
                  <p className="text-ink/60 text-sm mt-2 leading-relaxed">{v.d}</p>
                </motion.div>
              );
            })}
          </div>
          <div className="mt-16">
            <Link to="/hizmetler" className="group inline-flex items-center gap-2 text-tech font-semibold">
              Hizmetlerimizi İnceleyin <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      <CTA />
    </>
  );
}
